import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";

import clsx from "clsx";

import { makeStyles, Paper } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import TicketAiAnalysisDrawer from "../TicketAiAnalysisDrawer";
import MessageInput from "../MessageInput";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtonsCustom";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { ForwardMessageProvider } from "../../context/ForwarMessage/ForwardMessageContext";

import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import {
  loadHistoryScope,
  saveHistoryScope,
} from "../../utils/chatAgentHistoryScope";
import {
  loadSelectedPromptId,
  resolveSelectedPromptId,
  saveSelectedPromptId,
} from "../../utils/chatAgentPromptStorage";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TagsContainer } from "../TagsContainer";
import { isNil } from 'lodash';
import { EditMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

const drawerWidth = 320;

const normalizePromptOption = (prompt) => ({
  id: prompt.id,
  name: prompt.name,
  model: prompt.model,
});

const resolveChatAgentConfig = async () => {
  try {
    const { data } = await api.get("/chat-agent/config");
    if (data.prompts?.length) {
      return data;
    }
  } catch {
    // fallback below
  }

  const { data: promptData } = await api.get("/prompt");
  const prompts = (promptData.prompts || []).map(normalizePromptOption);

  return {
    configured: prompts.length > 0,
    prompts,
  };
};

const syncPromptSelection = (data, companyId, setSelectedPromptId) => {
  const prompts = data.prompts || [];
  setSelectedPromptId((currentId) => {
    const resolved = resolveSelectedPromptId({
      companyId,
      prompts,
      currentId,
    });
    if (resolved) return resolved;
    return prompts[0]?.id ? Number(prompts[0].id) : null;
  });
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    marginLeft: -drawerWidth,
    transition: theme.transitions.create(["margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShiftRight: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    marginRight: 0,
    transition: theme.transitions.create(["margin"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },

  mainWrapperShiftLeft: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    marginLeft: 0,
    transition: theme.transitions.create(["margin"], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { user, socket } = useContext(AuthContext);
  const { setTabOpen } = useContext(TicketsContext);
  const { companyId } = user || {};

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiRefining, setAiRefining] = useState(false);
  const [aiAnalyses, setAiAnalyses] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [chatAgentConfig, setChatAgentConfig] = useState(null);
  const [selectedPromptId, setSelectedPromptId] = useState(() =>
    companyId ? loadSelectedPromptId(companyId) : null
  );
  const [historyScope, setHistoryScope] = useState(() =>
    companyId ? loadHistoryScope(companyId) : "week"
  );
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});
  const [dragDropFiles, setDragDropFiles] = useState([]);

  useEffect(() => {
    if (companyId) {
      setHistoryScope(loadHistoryScope(companyId));
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      const stored = loadSelectedPromptId(companyId);
      if (stored) {
        setSelectedPromptId(stored);
      }
    }
  }, [companyId]);

  useEffect(() => {
    const fetchChatAgentConfig = async () => {
      try {
        const data = await resolveChatAgentConfig();
        setChatAgentConfig(data);
        syncPromptSelection(data, companyId, setSelectedPromptId);
      } catch {
        setChatAgentConfig({ configured: false, prompts: [] });
      }
    };
    if (companyId) {
      fetchChatAgentConfig();
    }
  }, [companyId]);

  const refreshChatAgentConfig = useCallback(async () => {
    try {
      const data = await resolveChatAgentConfig();
      setChatAgentConfig(data);
      setSelectedPromptId((currentId) => {
        const resolved = resolveSelectedPromptId({
          companyId,
          prompts: data.prompts || [],
          currentId,
        });
        if (resolved) return resolved;
        return currentId;
      });
      toast.success(i18n.t("ticketAiAnalysis.configRefreshed"));
    } catch {
      setChatAgentConfig({ configured: false, prompts: [] });
      toastError(i18n.t("ticketAiAnalysis.configRefreshFailed"));
    }
  }, [companyId]);

  const handleHistoryScopeChange = useCallback(
    (scope) => {
      setHistoryScope(scope);
      if (companyId) {
        saveHistoryScope(companyId, scope);
      }
    },
    [companyId]
  );

  const handlePromptChange = useCallback(
    (promptId) => {
      const normalizedId = Number(promptId);
      setSelectedPromptId(normalizedId);
      saveSelectedPromptId(companyId, normalizedId);
    },
    [companyId]
  );

  const fetchAiAnalyses = useCallback(async (numericTicketId) => {
    if (!numericTicketId) return;
    try {
      const { data } = await api.get(`/tickets/${numericTicketId}/ai-analysis`);
      const list = data.analyses || [];
      setAiAnalyses(list);
      if (list.length) {
        setSelectedAnalysisId((prev) => prev || list[0].id);
      }
    } catch (err) {
      toastError(err);
    }
  }, []);

  useEffect(() => {
    if (ticket?.id) {
      fetchAiAnalyses(ticket.id);
    }
  }, [ticket?.id, fetchAiAnalyses]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {

          if (!isNil(ticketId) && ticketId !== "undefined") {

            const { data } = await api.get("/tickets/u/" + ticketId);

            setContact(data.contact);
            // setWhatsapp(data.whatsapp);
            // setQueueId(data.queueId);
            setTicket(data);
            if (["pending", "open", "group"].includes(data.status)) {
              setTabOpen(data.status);
            }
            setLoading(false);
          }
        } catch (err) {
          history.push("/tickets");   // correção para evitar tela branca uuid não encontrado Feito por Altemir 16/08/2023
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, user, history]);

  useEffect(() => {
    if (!ticket && !ticket.id && ticket.uuid !== ticketId && ticketId === "undefined") {
      return;
    }

    if (user.companyId) {
      //    const socket = socketManager.GetSocket();

      const onConnectTicket = () => {
        socket.emit("joinChatBox", `${ticket.id}`);
      }

      const onCompanyTicket = (data) => {
        if (data.action === "update" && data.ticket.id === ticket?.id) {
          setTicket(data.ticket);
        }

        if (data.action === "delete" && data.ticketId === ticket?.id) {
          history.push("/tickets");
        }
      };

      const onCompanyContactTicket = (data) => {
        if (data.action === "update") {
          // if (isMounted) {
          setContact((prevState) => {
            if (prevState.id === data.contact?.id) {
              return { ...prevState, ...data.contact };
            }
            return prevState;
          });
          // }
        }
      };

      socket.on("connect", onConnectTicket)
      socket.on(`company-${companyId}-ticket`, onCompanyTicket);
      socket.on(`company-${companyId}-contact`, onCompanyContactTicket);

      return () => {

        socket.emit("joinChatBoxLeave", `${ticket.id}`);
        socket.off("connect", onConnectTicket);
        socket.off(`company-${companyId}-ticket`, onCompanyTicket);
        socket.off(`company-${companyId}-contact`, onCompanyContactTicket);
      };
    }
  }, [ticketId, ticket, history]);

  const handleDrawerOpen = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  const handleAiDrawerClose = useCallback(() => {
    setAiDrawerOpen(false);
  }, []);

  const handleOpenAiDrawer = useCallback(() => {
    setAiDrawerOpen(true);
  }, []);

  const handleAnalyzeWithAi = useCallback(async () => {
    if (!ticket?.id) return;
    setAiDrawerOpen(true);
    setAiAnalyzing(true);
    try {
      const payload = {
        ...(selectedPromptId ? { promptId: selectedPromptId } : {}),
        historyScope,
      };
      const { data } = await api.post(`/tickets/${ticket.id}/ai-analysis`, payload, {
        timeout: 120000,
      });
      const newItem = {
        id: data.analysisId,
        analysis: data.analysis,
        promptId: data.promptId,
        promptName: data.promptName,
        userName: user?.name || "",
        createdAt: data.createdAt,
        historyScope: data.historyScope,
        messageCount: data.messageCount,
      };
      setAiAnalyses((prev) => [newItem, ...prev.filter((item) => item.id !== newItem.id)]);
      setSelectedAnalysisId(newItem.id);
    } catch (err) {
      toastError(err);
    } finally {
      setAiAnalyzing(false);
    }
  }, [ticket?.id, selectedPromptId, historyScope, user?.name]);

  const handleRefineAnalysis = useCallback(
    async (analysisId, instruction) => {
      if (!ticket?.id || !analysisId || !instruction?.trim()) return;
      setAiRefining(true);
      try {
        const payload = {
          analysisId,
          instruction: instruction.trim(),
          ...(selectedPromptId ? { promptId: selectedPromptId } : {}),
        };
        const { data } = await api.post(
          `/tickets/${ticket.id}/ai-analysis/refine`,
          payload,
          { timeout: 120000 }
        );
        const newItem = {
          id: data.analysisId,
          analysis: data.analysis,
          promptId: data.promptId,
          promptName: data.promptName,
          userName: user?.name || "",
          createdAt: data.createdAt,
          historyScope: data.historyScope,
          messageCount: data.messageCount,
          refinedFrom: data.refinedFrom,
          agentInstruction: data.agentInstruction,
        };
        setAiAnalyses((prev) => [newItem, ...prev.filter((item) => item.id !== newItem.id)]);
        setSelectedAnalysisId(newItem.id);
        toast.success(i18n.t("ticketAiAnalysis.refineSuccess"));
      } catch (err) {
        toastError(err);
      } finally {
        setAiRefining(false);
      }
    },
    [ticket?.id, selectedPromptId, user?.name]
  );

  const renderMessagesList = () => {
    return (
      <>
        <MessagesList
          isGroup={ticket.isGroup}
          onDrop={setDragDropFiles}
          whatsappId={ticket.whatsappId}
          queueId={ticket.queueId}
          channel={ticket.channel}
        >
        </MessagesList>
        <MessageInput
          ticketId={ticket.id}
          ticketStatus={ticket.status}
          ticketChannel={ticket.channel}
          droppedFiles={dragDropFiles}
          contactId={contact.id}
          contactName={contact?.name || contact?.number || ""}
          ticketWhatsappId={ticket.whatsappId}
          ticketQueueId={ticket.queueId}
        />
      </>
    );
  };


  return (
    <div className={classes.root} id="drawer-container">
      <TicketAiAnalysisDrawer
        open={aiDrawerOpen}
        onClose={handleAiDrawerClose}
        analyzing={aiAnalyzing}
        refining={aiRefining}
        onAnalyze={handleAnalyzeWithAi}
        onRefine={handleRefineAnalysis}
        analyses={aiAnalyses}
        selectedAnalysisId={selectedAnalysisId}
        onSelectAnalysis={setSelectedAnalysisId}
        chatAgentConfig={chatAgentConfig}
        selectedPromptId={selectedPromptId}
        onPromptChange={handlePromptChange}
        historyScope={historyScope}
        onHistoryScopeChange={handleHistoryScopeChange}
        onRefreshConfig={refreshChatAgentConfig}
      />

      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShiftRight]: drawerOpen,
          [classes.mainWrapperShiftLeft]: aiDrawerOpen,
        })}
      >
        {/* <div id="TicketHeader"> */}
        <TicketHeader loading={loading}>
          {ticket.contact !== undefined && (
            <div id="TicketHeader">
              <TicketInfo
                contact={contact}
                ticket={ticket}
                onClick={handleDrawerOpen}
              />
            </div>
          )}
          <TicketActionButtons
            ticket={ticket}
            onOpenAiDrawer={handleOpenAiDrawer}
          />
        </TicketHeader>
        {/* </div> */}
        <Paper>
          <TagsContainer contact={contact} />
        </Paper>
        <ReplyMessageProvider>
          <ForwardMessageProvider>
            <EditMessageProvider>
              {renderMessagesList()}
            </EditMessageProvider>
          </ForwardMessageProvider>
        </ReplyMessageProvider>
      </Paper>

      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
        ticket={ticket}
      />

    </div>
  );
};

export default Ticket;
